import * as React from 'react'
import { Grid, Box, Card, CardContent, Stack, Typography, FormControl, InputLabel, Select, MenuItem, CircularProgress, Divider, Button, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { PieChart } from '@mui/x-charts/PieChart';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// Translation
import { useTranslation } from 'react-i18next';

import MainLayout from '../components/layout/Main';
import UsageCalendar from '../components/UsageCalendar';
import ActivityFeed from '../components/ActivityFeed';
import StressPrediction from '../components/StressPrediction';
import QuickStats from '../components/QuickStats';
import api from "../api"


function secondsToTimeString(d) {
  let seconds = d % 60
  d = Math.floor(d / 60)
  let minutes = d % 60
  let hours = Math.floor(d / 60)
  
  // Pad minutes and seconds with leading zeros
  const paddedMinutes = minutes < 10 ? `0${minutes}` : minutes
  const paddedSeconds = seconds < 10 ? `0${seconds}` : seconds
  
  return `${hours}:${paddedMinutes}:${paddedSeconds}`
}

function roundPercent(value) {
  return Math.round((value) * 10000) / 100
}

function Dashboard() {
  const [devices, setDevices] = React.useState([])
  const [usageTime, setUsageTime] = React.useState([])
  const [stressData, setStressData] = React.useState([])
  const [stressChartData, setStressChartData] = React.useState([])
  const [stressChartData2, setStressChartData2] = React.useState([])
  const [stressDataTime, setStressDataTime] = React.useState([])

  const [selectedDevice, setSelectedDevice] = React.useState('');

  const [loading, setLoading] = React.useState(false)

  const [startDate, setStartDate] = React.useState(dayjs().subtract(1, 'day'));
  const [endDate, setEndDate] = React.useState(dayjs());

  const navigate = useNavigate()

  const { t } = useTranslation()
  const theme = useTheme()

  React.useEffect(() => {
    setLoading(true)
    getDevices()
    document.title = t("dashboard.page_title")
  }, [])

  React.useEffect(() => {
    try {
      setSelectedDevice(devices[0].id)
    } catch (err) {

    }
  }, [devices])

  React.useEffect(() => {
    getUsageTime(selectedDevice)
    getStressStats(selectedDevice)
  }, [selectedDevice])

  React.useEffect(() => {
    try {
      let total = stressData["no_load"] + stressData["load"]["intensive"] + stressData["load"]["dangerous"]
      setStressChartData([
        { label: t("dashboard.without_stress_short"), value: roundPercent(stressData["no_load"] / total), color: '#4caf50'},
        { label: t("dashboard.with_stress_short"), value: roundPercent((stressData["load"]["intensive"] + stressData["load"]["dangerous"]) / total), color: '#ff9800'},
      ])
      total = stressData["load"]["intensive"] + stressData["load"]["dangerous"]
      setStressChartData2([
        { label: t("dashboard.intensive_text"), value: roundPercent(stressData["load"]["intensive"] / total), color: '#fbc02d'},
        { label: t("dashboard.dangerous_text"), value: roundPercent(stressData["load"]["dangerous"] / total), color: '#f44336'},
      ])
      setStressDataTime([secondsToTimeString(stressData["no_load"]), secondsToTimeString(stressData["load"]["intensive"] + stressData["load"]["dangerous"]), secondsToTimeString(stressData["load"]["intensive"]), secondsToTimeString(stressData["load"]["dangerous"])])
    } catch (err) {
      // console.log(err)
    }
    setLoading(false)
  }, [stressData])

  const getDevices = () => {
    api
      .get("/api/devices/")
      .then((res) => res.data)
      .then((data) => {
        setDevices(data)
      })
      .catch((err) => console.log(err))
  }

  const getUsageTime = (id, date_start=null, date_end=null) => {
    if (id != '') {
      api
        .get(`/api/device/usage/${id}/`, { params: { "date_start": date_start, "date_end": date_end } })
        .then((res) => res.data)
        .then((data) => {
          setUsageTime(secondsToTimeString(data['usage_time']))
        })
        .catch((err) => console.log(err))
    }
  }

  const getStressStats = (id, date_start=null, date_end=null) => {
    if (id != '') {
      api
        .get(`/api/device/stress/${id}/`, { params: { "date_start": date_start, "date_end": date_end } })
        .then((res) => res.data)
        .then((data) => {
          setStressData(data['stress_data'])
        })
        .catch((err) => console.log(err))
    }
  }

  const handleDeviceChange = (event) => {
    setSelectedDevice(event.target.value);
  };

  const handleDatePickerClick = (event) => {
    getStressStats(selectedDevice, startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD"))
    getUsageTime(selectedDevice, startDate.format("YYYY-MM-DD"), endDate.format("YYYY-MM-DD"))
  }

  const handleDetailsRedirect = (event) => {
    navigate("/details", { state: { selectedDevice: selectedDevice } })
  }

  return (
    <MainLayout activeComponent='/'>
      <CircularProgress sx={{ display: loading ? '' : 'none'}}/>
      <Grid container spacing={3} sx={{ display: loading ? 'none' : ''}}>
        <Grid item lg={8} xs={12}>
          <Card 
            variant='outlined' 
            sx={{ 
              borderRadius: '16px',
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            }}
          >
            <CardContent>
              <Typography 
                variant='h5' 
                sx={{ 
                  marginBottom: 3, 
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  borderLeft: '4px solid #dc3545',
                  paddingLeft: 2,
                }}
              >
                {t("dashboard.chart_title")}
              </Typography>
              <Box sx={{ display: stressData["no_load"] > 0 ? '' : 'none' }}>
                <Stack spacing={2} direction="row">
                  <Stack direction="column" spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', paddingLeft: 8}}>
                    <Typography variant='h6' sx={{ color: '#4caf50'}}>{t("dashboard.without_stress_label")}</Typography>
                    <Tooltip title={t("dashboard.time_format_tooltip") || "Time format: hours:minutes:seconds"} arrow>
                      <Typography variant='h6' sx={{ cursor: 'help' }}>
                        {stressChartData[0] ? stressChartData[0].value : null}% ({stressDataTime[0]})
                      </Typography>
                    </Tooltip>
                    <Typography variant='h6' sx={{ color: '#ff9800' }}>{t("dashboard.with_stress_label")}</Typography>
                    <Tooltip title={t("dashboard.time_format_tooltip") || "Time format: hours:minutes:seconds"} arrow>
                      <Typography variant='h6' sx={{ cursor: 'help' }}>
                        {stressChartData[1] ? stressChartData[1].value : null}% ({stressDataTime[1]})
                      </Typography>
                    </Tooltip>
                  </Stack>
                  <PieChart 
                  series={[
                    {
                      data: stressChartData,
                      innerRadius: 40,
                      outerRadius: 120,
                      paddingAngle: 2,
                      cornerRadius: 5,
                      highlighted: { 
                        additionalRadius: 10 
                      },
                      highlightScope: {
                        highlight: 'item',
                      }
                    },
                  ]}
                  margin={{ right: 5 }}
                  width={300}
                  height={300}
                  legend={{ hidden: true }}
                />
              </Stack>
              <Divider/>
              <Stack spacing={2} direction="row">
                <Stack direction="column" spacing={2} sx={{ alignItems: 'center', justifyContent: 'center', paddingLeft: 8}}>
                  <Typography variant='h5'>{t("dashboard.stress_type")}</Typography>
                  <Typography variant='h6' sx={{ color: '#fbc02d'}}>{t("dashboard.intensive_text")}</Typography>
                  <Tooltip title={t("dashboard.time_format_tooltip") || "Time format: hours:minutes:seconds"} arrow>
                    <Typography variant='h6' sx={{ cursor: 'help' }}>
                      {stressChartData2[0] ? stressChartData2[0].value : null}% ({stressDataTime[2]})
                    </Typography>
                  </Tooltip>
                  <Typography variant='h6' sx={{ color: '#f44336' }}>{t("dashboard.dangerous_text")}</Typography>
                  <Tooltip title={t("dashboard.time_format_tooltip") || "Time format: hours:minutes:seconds"} arrow>
                    <Typography variant='h6' sx={{ cursor: 'help' }}>
                      {stressChartData2[1] ? stressChartData2[1].value : null}% ({stressDataTime[3]})
                    </Typography>
                  </Tooltip>
                </Stack>
                <PieChart 
                  series={[
                    {
                      paddingAngle: 2,
                      cornerRadius: 5,
                      innerRadius: 40,
                      outerRadius: 120,
                      data: stressChartData2,
                      highlighted: { 
                        additionalRadius: 10 
                      },
                      highlightScope: {
                        highlight: 'item',
                      }
                    },
                  ]}
                  margin={{ right: 5 }}
                  width={300}
                  height={300}
                  legend={{ hidden: true }}
                />
              </Stack>
              <Divider />
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'end', marginTop: 2 }}>
                <Button endIcon={<ArrowForwardIcon />} variant="contained" onClick={handleDetailsRedirect}>
                  {t("dashboard.detailed_view_button")}
                </Button>
              </Box>
              </Box>
              <Box sx={{ display: stressData["no_load"] > 0 ? 'none' : '' }}>
                <Typography variant='h4' sx={{ marginTop: 1 }}>{t("dashboard.no_data_range_error")}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item lg={4} xs={12}>
          <Card 
            variant='outlined' 
            sx={{ 
              borderRadius: '16px', 
              marginBottom: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            }}
          >
            <CardContent>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: theme.palette.text.primary,
                    mb: 2 
                  }}
                >
                  {t("dashboard.choose_device_text")}
                </Typography>
                {/* <FormControl sx={{ marginY: 2 }} fullWidth size="small"> */}
                <FormControl sx={{ marginTop: 2 }} fullWidth size="small">
                  <InputLabel id="deivce-select-label">{t("dashboard.choose_device_label")}</InputLabel>
                  <Select
                    labelId="device-select-label"
                    id="device"
                    value={selectedDevice}
                    label={t("dashboard.choose_device_label")}
                    onChange={handleDeviceChange}
                  >
                    {devices.map((item) => <MenuItem value={item.id} key={item.id}>{item.name || `${t("dashboard.vehicle")} ${item.id}`}</MenuItem>)}
                  </Select>
                </FormControl>
                {/* <Typography variant="h5">Wybierz sesję</Typography>
                <FormControl sx={{ marginY: 2 }} fullWidth size="small">
                  <InputLabel id="session-select-label">Wybór sesji</InputLabel>
                  <Select
                    labelId="session-select-label"
                    id="session"
                    value={session}
                    label="Wybór sesji"
                    onChange={handleSessionChange}
                  >
                    {records.map((item) => <MenuItem value={item.id}>Zapis z {new Date(item.created_at).toLocaleDateString('pl-PL', { weekday:"long", year:"numeric", month:"short", day:"numeric", hour: "2-digit", minute: "2-digit", second: '2-digit'})}</MenuItem>)}
                  </Select>
                </FormControl> */}
              </Box>
            </CardContent>
          </Card>
          <Card 
            variant='outlined' 
            sx={{ 
              borderRadius: '16px', 
              marginBottom: 3,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            }}
          >
            <CardContent>
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600, 
                    color: theme.palette.text.primary,
                    mb: 1 
                  }}
                >
                  {t("dashboard.time_range_text")}
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DatePicker', 'DatePicker']} sx={{ marginTop: 1 }}>
                    <DatePicker
                      label={t("dashboard.date_start_label")}
                      value={startDate}
                      maxDate={endDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      disableFuture 
                    />
                    <DatePicker
                      label={t("dashboard.date_end_label")}
                      value={endDate}
                      minDate={startDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      disableFuture 
                    />
                  </DemoContainer>
                </LocalizationProvider>
                <Button fullWidth variant="contained" sx={{ marginTop: 2 }} onClick={handleDatePickerClick}>
                  {t("dashboard.filter_button_text")}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats */}
        <Grid item xs={12}>
          <QuickStats deviceId={selectedDevice} />
        </Grid>
        
        {/* Usage Calendar */}
        <Grid item xs={12}>
          <UsageCalendar 
            deviceId={selectedDevice} 
            year={dayjs().year()} 
            month={dayjs().month() + 1}  
          />
        </Grid>

        {/* Stress Prediction */}
        <Grid item xs={12} lg={6}>
          <StressPrediction deviceId={selectedDevice} />
        </Grid>

        {/* Activity Feed */}
        <Grid item xs={12} lg={6}>
          <ActivityFeed 
            deviceId={selectedDevice}
            limit={10}
            autoRefresh={false}
          />
        </Grid>
      </Grid>
    </MainLayout>      
  );
}

export default Dashboard;
